import { useCallback, useContext, useMemo } from 'react';

import { bytesToUnit, sumDataTransferTraffic, toNumOrZero } from '@cast/utils';

import { trafficTickFormatter } from 'features/cluster/cost-report/network-cost/_utils';

import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { useCostReportTimeRangeSelection } from 'features/cost-report/hooks/useCostReportTimeRangeSelection';
import { buildCostReportAnalyzePrompt } from 'features/cost-report/utils/buildCostReportAnalyzePrompt';
import { NetworkCostOverTimeContext } from '../../../providers/NetworkCostOverTimeProvider';

import { useDataTransferChartDatapoints } from '../../_hooks/useDataTransferChartDatapoints';
import { useDataTransferSummary } from '../../_hooks/useDataTransferSummary';
import { ChartSectionBox } from '../_components/ChartSectionBox';
import { NetworkChart } from '../_components/NetworkChart';
import { makeNetworkChartTooltip } from '../_components/NetworkChartTooltip';
import { NetworkMetricsBox } from '../_components/NetworkMetricsBox';
import { useCluster } from '@cast/shared-cluster-context-data-access';

import { Stack } from '@cast/design-system';

const EMPTY_VALUE = '--';

export const NetworkTrafficSection = () => {
  const { cluster } = useCluster();
  const { isDefaultFilter, timezone } = useCostReportContext();
  const { getRange } = useCostReportTimeRangeSelection();
  const { current } = useContext(NetworkCostOverTimeContext);
  const chartData = useDataTransferChartDatapoints('traffic');
  const { traffic } = useDataTransferSummary();

  const data = current.data.items;
  const accumulated = useMemo(() => {
    if (data.length) {
      if (isDefaultFilter) {
        const today = data[data.length - 1];
        const egress = sumDataTransferTraffic(today.egress);
        const ingress = sumDataTransferTraffic(today.ingress);
        return {
          total: egress + ingress,
          egress,
          ingress,
        };
      } else {
        return {
          total: traffic.egress + traffic.ingress,
          egress: traffic.egress,
          ingress: traffic.ingress,
        };
      }
    }
    return undefined;
  }, [data, traffic, isDefaultFilter]);

  const tooltipFormatterFn = useCallback((v: number) => {
    const [value, valueUnit] = bytesToUnit(v, 3);
    return (
      <>
        {value} {valueUnit}
      </>
    );
  }, []);

  const { tooltipHeader, tooltipFormatValue } = makeNetworkChartTooltip({
    label: 'Network traffic',
    timezone,
    getValue: (data) => toNumOrZero(data.egress) + toNumOrZero(data.ingress),
    formatValue: tooltipFormatterFn,
  });

  return (
    <ChartSectionBox>
      <Stack
        display="grid"
        gridTemplateColumns="repeat(2, 1fr)"
        gap={24}
        p="12px 20px"
      >
        <NetworkMetricsBox
          title={isDefaultFilter ? 'current day traffic' : 'total traffic'}
          primaryValue={
            !current.isLoading && accumulated?.total
              ? bytesToUnit(accumulated.total, 2).join(' ')
              : EMPTY_VALUE
          }
        />
        <Stack
          display="grid"
          gridTemplateColumns="repeat(2, 1fr)"
          gap={24}
          px={16}
        >
          <NetworkMetricsBox
            markerColor="indigo.200"
            title="egress"
            primaryValue={
              !current.isLoading && accumulated?.egress
                ? bytesToUnit(accumulated.egress, 2).join(' ')
                : EMPTY_VALUE
            }
            primaryValueProps={{ variant: 'h6', lineHeight: '20px' }}
          />
          <NetworkMetricsBox
            markerColor="grey.700"
            title="ingress"
            primaryValue={
              !current.isLoading && accumulated?.ingress
                ? bytesToUnit(accumulated.ingress, 2).join(' ')
                : EMPTY_VALUE
            }
            primaryValueProps={{ variant: 'h6', lineHeight: '20px' }}
          />
        </Stack>
      </Stack>

      <NetworkChart
        data={chartData}
        isLoading={current.isLoading}
        unit="GiB"
        tooltipHeader={tooltipHeader}
        tooltipFormatValue={tooltipFormatValue}
        yAxisProps={{
          tickFormatter: trafficTickFormatter,
        }}
        timezone={timezone}
        buildPrompt={buildCostReportAnalyzePrompt({
          scopeName: cluster?.name,
          context: 'network traffic',
          getDefaultRange: getRange,
        })}
      />
    </ChartSectionBox>
  );
};
